import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { FaSave } from "react-icons/fa";
import { Button } from "components/common";
import { GEIST_INPUT } from "../common/UnderlineInput";
import { updateDigitalSignatureConfig } from "../common/settingsApi";
import { TH, TD, ROW, TONE, Chip, SettingsCard, GroupRow } from "./settingsKit";
import SignerCertificateRow from "./SignerCertificateRow";

const COLS = 4;
const PEM_BLOCK = /-----BEGIN CERTIFICATE-----[\s\S]*?-----END CERTIFICATE-----/g;
const PROFILE_FIELDS = ["portal_user_id", "name", "user_id", "serial_number", "certificate_pem"];

const settingValue = (data, key) => data?.settings?.[key]?.value || "";
const initialRows = (data) => (data?.signer_certificates?.rows || []).map((r, i) => ({ ...r, key: `${r.portal_user_id || "row"}-${i}`, dirty: false }));
const hasContent = (r) => !!(r.user_id || r.serial_number || r.certificate_pem);
const toProfile = (r) => Object.fromEntries(PROFILE_FIELDS.map((f) => [f, r[f] ?? null]));

/**
 * Chứng thư SmartCA của người ký (Head of Finance, BOD, BOD stand-in — lấy từ tab
 * Signers, không cần tự thêm). Mỗi người: SmartCA account ID → "Get from SmartCA"
 * điền serial + chứng thư. Công tắc "Require" chỉ bật được khi mọi người đã sẵn sàng.
 */
const SignerCertificateSettings = ({ data, onSaved }) => {
  const [rows, setRows] = useState(() => initialRows(data));
  const [ca, setCa] = useState(() => settingValue(data, "SMARTCA_TRUSTED_CA_PEM"));
  const [caOpen, setCaOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows(initialRows(data));
    setCa(settingValue(data, "SMARTCA_TRUSTED_CA_PEM"));
  }, [data]);

  // "Use SmartCA" (Digital signature card above) = Head of Finance / BOD must sign with it.
  const savedRequired = settingValue(data, "SMARTCA_ENABLED") === "true";
  const savedCa = settingValue(data, "SMARTCA_TRUSTED_CA_PEM");
  const dirty = rows.some((r) => r.dirty) || ca !== savedCa
    || rows.length !== (data?.signer_certificates?.rows || []).length;
  const issues = data?.signer_certificates?.readiness_issues || [];
  const requiredRows = rows.filter((r) => r.required);
  const readyCount = requiredRows.filter((r) => !r.dirty && r.certificate?.state === "ok").length;
  const caCount = (ca.match(PEM_BLOCK) || []).length;

  const updateRow = (index, patch) => setRows((list) => list.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const removeRow = (index) => setRows((list) => list.flatMap((r, i) => {
    if (i !== index) return [r];
    // A required person stays listed — only their SmartCA details are cleared.
    return r.required ? [{ ...r, user_id: "", serial_number: "", certificate_pem: "", certificate: { state: "missing" }, dirty: true }] : [];
  }));
  const addRow = () => setRows((list) => [...list, { key: `new-${Date.now()}`, required: false, roles: [], certificate: { state: "missing" }, dirty: true }]);
  const caFound = (pem) => setCa((current) => (current.trim() ? current : pem));

  const save = async () => {
    setSaving(true);
    try {
      const profiles = rows.filter((r) => hasContent(r) || (!r.required && r.portal_user_id)).map(toProfile);
      const res = await updateDigitalSignatureConfig({
        SMARTCA_SIGNER_PROFILES: JSON.stringify(profiles),
        SMARTCA_TRUSTED_CA_PEM: ca,
      });
      onSaved?.(res.data);
      toast.success("Signer certificates saved.");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Cannot save signer certificates.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard
      title="Signer certificates"
      subtitle="Head of Finance, BOD and BOD stand-ins sign each payment with their own SmartCA certificate. Private keys stay with VNPT."
      status={(
        <>
          <Chip tone={readyCount === requiredRows.length && requiredRows.length > 0 ? TONE.ok : TONE.warn}>
            {readyCount}/{requiredRows.length} signers ready
          </Chip>
          {savedRequired && issues.length > 0 ? <Chip tone={TONE.bad} icon="bi-exclamation-triangle-fill">SmartCA on · incomplete</Chip>
            : savedRequired ? <Chip tone={TONE.ok} icon="bi-lock-fill">SmartCA on</Chip> : <Chip tone={TONE.muted}>SmartCA off</Chip>}
        </>
      )}
      actions={(
        <Button variant="primary" size="sm" leftIcon={FaSave} disabled={saving || !dirty} onClick={save}>
          {saving ? "Saving..." : "Save"}
        </Button>
      )}
    >
      <div className="tw-overflow-x-auto">
        <table className="tw-w-full tw-border-collapse">
          <thead>
            <tr className={`tw-bg-geist-bg-2 ${ROW}`}>
              <th className={TH}>Signer</th>
              <th className={TH}>SmartCA account</th>
              <th className={TH}>Certificate</th>
              <th className={TH} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {requiredRows.length === 0 && (
              <tr className={ROW}>
                <td className={`${TD} tw-text-[12px] tw-text-geist-fg-3`} colSpan={COLS}>
                  Choose the Head of Finance and BOD in the Signers tab first — they appear here automatically.
                </td>
              </tr>
            )}
            {rows.map((row, index) => (
              <SignerCertificateRow key={row.key} row={row} onCaFound={caFound}
                onChange={(patch) => updateRow(index, patch)} onRemove={() => removeRow(index)} />
            ))}
            <tr className={ROW}>
              <td className={TD} colSpan={COLS}>
                <Button variant="secondary" size="sm" onClick={addRow}>Add another signer</Button>
              </td>
            </tr>

            <GroupRow colSpan={COLS} icon="bi-shield-check" title="Trusted issuing CA"
              subtitle="certificates are checked against it"
              status={caCount > 0 ? <Chip tone={TONE.ok} icon="bi-check2">{caCount} CA certificate{caCount > 1 ? "s" : ""}</Chip>
                : <Chip tone={TONE.warn}>Not set</Chip>} />
            <tr className={ROW}>
              <td className={TD} colSpan={COLS}>
                <div className="tw-text-[12px] tw-text-geist-fg-3">
                  Filled in automatically by “Get from SmartCA”. Paste the VNPT SmartCA CA chain here only if it is missing.
                  <button type="button" onClick={() => setCaOpen((v) => !v)}
                    className="tw-ml-2 tw-border-0 tw-bg-transparent tw-p-0 tw-text-[12px] tw-font-medium tw-text-geist-accent hover:tw-underline">
                    {caOpen ? "Hide" : caCount > 0 ? "View / edit" : "Paste CA"}
                  </button>
                </div>
                {caOpen && (
                  <textarea aria-label="Trusted issuing CA certificate (PEM)" rows={5} value={ca} onChange={(e) => setCa(e.target.value)}
                    placeholder={"-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"}
                    className={`${GEIST_INPUT} tw-mt-2 tw-h-auto tw-py-2 tw-font-mono tw-text-[11px] tw-leading-snug`} />
                )}
              </td>
            </tr>

            {issues.length > 0 && (
              <tr className={ROW}>
                <td className={TD} colSpan={COLS}>
                  <div className={`tw-rounded-geist-sm tw-px-3 tw-py-2 tw-text-[12px] ${savedRequired ? "tw-bg-[#fdf2f0] tw-text-[#8e2a18]" : "tw-bg-[#fff8ee] tw-text-[#a05e03]"}`}>
                    <div className="tw-font-semibold">
                      {savedRequired
                        ? "Use SmartCA is ON but setup is incomplete — Head of Finance and BOD cannot approve until this is fixed (or switch it off):"
                        : `Before "Use SmartCA" can be switched on${dirty ? " (as last saved)" : ""}:`}
                    </div>
                    <ul className="tw-m-0 tw-mt-1 tw-pl-4">
                      {issues.map((issue) => <li key={issue}>{issue}</li>)}
                    </ul>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </SettingsCard>
  );
};

SignerCertificateSettings.propTypes = {
  // GET digital_signature payload; null while the tab is still loading.
  data: PropTypes.shape({
    settings: PropTypes.objectOf(PropTypes.shape({ value: PropTypes.string })),
    signer_certificates: PropTypes.shape({
      rows: PropTypes.arrayOf(PropTypes.object),
      readiness_issues: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
  onSaved: PropTypes.func,
};

export default SignerCertificateSettings;
